import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './assets/styles/index.css'
// Phase 1 new style layers (order: tokens -> legacy general -> utilities)
import './assets/styles/tokens.css';
// general.css merged into utilities.css (see utilities.css legacy section)
import './assets/styles/utilities.css';

// Leaflet CSS imports - Important for Webpack bundling
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
// import 'react-leaflet-markercluster/dist/styles.min.css';

// Date formats are initialized lazily where needed to avoid unintended requests.

import App from './App'

import i18n from './i18n';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import configureStore from './store/redux/store/store';
import { fetchAnyUserWithVariableScheduleRule } from './store/redux/actions/userActionCreator';
import { generatePalette, setCSSColorVariables } from './utils';
import { setupAuthLifecycle } from './utils/authLifecycle';
import { isPublicRoute } from './utils/publicRoutes';
import { applyDocumentLocale } from './utils/documentLocale';

// Free-tier feature_locked rejections come back from the request interceptor as
// plain objects (e.g. { error: 'feature_locked', module: 'attendance', ... }).
// The interceptor already shows the user a single toast — but page-level fetches
// that don't catch (and there are many in the locked pages we still mount under
// LockedShell) bubble the raw object up as unhandled rejections, which show as
// `[object Object]` in console and trigger webpack-dev-server's red overlay.
// We swallow those here. Every other rejection passes through untouched so real
// bugs stay loud.
try {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { error?: unknown; errors?: unknown } | undefined;
    if (!reason || typeof reason !== 'object') return;
    const topLevel = (reason as { error?: unknown }).error === 'feature_locked';
    const inErrors =
      Array.isArray((reason as { errors?: unknown }).errors) &&
      ((reason as { errors: Array<{ code?: unknown }> }).errors.some(
        (e) => e?.code === 'feature_locked' || e?.code === 'FeatureLocked',
      ));
    if (topLevel || inErrors) {
      event.preventDefault();
    }
  });
} catch {}

const { store, persistor } = configureStore();
// Expose the Redux store + persistor for utility modules (e.g., axios
// interceptors, forceLogout) so they don't have to recreate them.
try {
  (globalThis as any).__APP_STORE__ = store;
  (globalThis as any).__APP_PERSISTOR__ = persistor;
} catch {}

// Wire UserManager events → Redux, plus cross-tab logout sync. Must run
// BEFORE i18n.loadNamespaces() so that any signinCallback / signinSilent
// that fires during React's initial render isn't dispatched into an
// unsubscribed event emitter.
try {
  setupAuthLifecycle(store, persistor!);
} catch (e) {
  console.error('[auth] setupAuthLifecycle failed:', e);
}
// Kick off initial fetches needed app-wide, but only after an access token exists
// AND the current route actually needs an authenticated user. On public routes
// (e.g. /p/<slug>), a stale token left in localStorage from a prior session
// would otherwise 401 this request and the response interceptor would force
// the anonymous visitor to /Account/Login — see utils/publicRoutes.ts.
try {
  if (isPublicRoute()) {
    // Intentional no-op on public pages.
  } else {
  // CO-342: read the persisted role alongside the token. The endpoint below is
  // deliberately Admin/Hr/Manager-only on the server (ShiftService.cs:163-170 returns
  // PermissionDenied for anyone else), so firing it for an ordinary Clocking employee
  // is a guaranteed 401 — twice on every page load, logged as a red console error on
  // the most-used screen in the product. Nothing is gained: the thunk stores `false`
  // on failure, which is the same value a non-privileged user would get anyway.
  let persistedIsPrivileged = false;
  const hasAccessToken = (() => {
    try {
      const persisted = localStorage.getItem('persist:root');
      if (persisted) {
        const root = JSON.parse(persisted);
        if (root && root.user) {
          const userState = JSON.parse(root.user);
          // Mirror the server's own predicate: isAdmin || isHr || isManager.
          persistedIsPrivileged =
            userState?.role === 'Admin' ||
            userState?.role === 'Hr' ||
            userState?.isManager === true;
          if (userState?.userAuthInfo?.access_token) return true;
        }
      }
      // Fallback: check OIDC storage directly
      const oidcKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('oidc.user:') || (key.startsWith('oidc.') && localStorage.getItem(key)?.includes('access_token'))
      );
      for (const key of oidcKeys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) return true;
        } catch {}
      }
    } catch {}
    return false;
  })();
  // CO-342: only privileged users may call this (see the comment above). A Clocking
  // employee skips it entirely and keeps the reducer's default `false`.
  if (hasAccessToken && persistedIsPrivileged) {
    store.dispatch<any>(fetchAnyUserWithVariableScheduleRule());
  }
  }
} catch {}

// Set the initial document direction + language from the PERSISTED LOCALE before the
// app mounts.
//
// CO-521: this used to key off `typeof userState.rtl === 'boolean'`. The server has
// never sent an `Rtl` field, so `INIT` wrote `undefined` into the store on every boot
// and the persisted `rtl` stopped being a boolean after the first login — which meant
// this whole block silently no-opped forever after. Deriving from `locale` (the field
// the server DOES send, and the one i18n.js:23 itself boots from) has no such failure
// mode.
//
// `lang` is set here too: the static shell ships `lang="en"` (index.html:2) and nothing
// else touches it until React mounts, so an Arabic user's first paint announced "en".
try {
  const persisted = localStorage.getItem('persist:root');
  if (persisted && typeof document !== 'undefined') {
    const root = JSON.parse(persisted);
    if (root && root.user) {
      const userState = JSON.parse(root.user);
      // Delegated to `utils/documentLocale` so the boot path and the runtime path (App.tsx's
      // effect, LanguageSwitch's click handler) cannot drift apart again.
      if (userState?.locale) applyDocumentLocale(String(userState.locale));
    }
  }
} catch {}


const renderApp = () => {
  createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>,
  );
};

// Preload all configured namespaces (plus fallbacks) to avoid keys rendering before HTTP backend finishes
const ns = Array.isArray((i18n.options as any)?.ns) ? (i18n.options as any).ns as string[] : ['common'];
const fallback = ((): string[] => {
  const f = (i18n.options as any)?.fallbackNS;
  if (!f) return [];
  return Array.isArray(f) ? f : [f];
})();

i18n
  .loadNamespaces([...new Set([...ns, ...fallback])])
  .then(renderApp)
  .catch(renderApp)
